<script setup lang="ts">
/* =============================================================
   STAY QUOTE
   =============================================================
   The answer for one berth, one ship, one stay: can it be booked,
   what does it cost, which price rule set that, and how will the
   owner confirm. Shown on the berth profile, beside "Book".

   The rule is shown in words ("Ships up to 135 m → €710 per
   24 h") so the price explains itself -- a number with no reason
   is what the brief says operators get today.
   ============================================================= */

import { computed } from 'vue'
import Badge from '@/components/ui/Badge.vue'
import Icon from '@/components/Icon.vue'
import { formatDuration, formatEur, rateConditionText, ratePriceText } from '@/lib/bookingLabels'
import { bookingModeText, resultStatus } from '@/lib/searchLabels'
import type { BerthRate, BerthResult } from '@/lib/types'

const props = defineProps<{
  result: BerthResult
  hasStay: boolean
  rates: BerthRate[]
  from?: string
  to?: string
}>()

const status = computed(() => resultStatus(props.result, props.hasStay))
const rule = computed(() => props.rates.find((r) => r.id === props.result.rate_id) ?? null)
const ruleIndex = computed(() => (rule.value ? props.rates.indexOf(rule.value) + 1 : 0))
</script>

<template>
  <div class="quote">
    <div class="head">
      <Badge :tone="status.tone">{{ status.label }}</Badge>
      <span class="mode">
        <Icon :name="result.booking_mode === 'instant' ? 'check' : 'clock'" :size="14" />
        {{ bookingModeText(result.booking_mode, result.response_hours) }}
      </span>
    </div>

    <template v-if="hasStay && result.price_eur !== null">
      <p class="amount">{{ formatEur(result.price_eur) }}</p>
      <p class="sub">
        net of VAT · {{ from && to ? formatDuration(from, to) : '' }}
      </p>
      <p v-if="rule" class="rule">
        Rule {{ ruleIndex }}: {{ rateConditionText(rule) }} → {{ ratePriceText(rule) }}
      </p>
    </template>

    <p v-if="status.detail" class="detail" :class="status.tone">{{ status.detail }}</p>
  </div>
</template>

<style scoped>
.quote {
  display: flex;
  flex-direction: column;
  gap: var(--space-3xs);
}

.head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-xs);
  margin-bottom: var(--space-2xs);
}

.mode {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3xs);
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.amount {
  font-size: var(--text-xl, 1.75rem);
  font-weight: var(--weight-bold);
  font-variant-numeric: tabular-nums;
  letter-spacing: var(--tracking-tight);
  color: var(--color-heading);
}

.sub {
  font-size: var(--text-xs);
  color: var(--color-text-faint);
}

.rule {
  margin-top: var(--space-2xs);
  padding: var(--space-2xs) var(--space-xs);
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  background-color: var(--color-surface-sunken);
  border-radius: var(--radius-sm);
}

.detail {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.detail.danger {
  color: var(--color-danger);
}

.detail.warning {
  color: var(--color-accent-2);
}
</style>
